import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaEdit, FaTimesCircle } from "react-icons/fa";
import {
  signUpEventAdmin,
  updateEventMaster,
  getLookupValue,
  getOccasionsByEventId,
  FRONTEND_URL,
} from "../../server/Api";
import toast from "react-hot-toast";
import "../../assets/css/event.css";
import function1 from "../../assets/images/function1.svg";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setSelectedEvent } from "../../redux/slices/authSlice";
import { getAllEvents  } from "../../utils/getAllEventMasterDetails";
import {
  EVENT_ACCESS_MODULES,
  isSuperAdminUser,
} from "../../utils/eventAccessModules";
import {
  parseCapacity,
  sumOccasionCapacities,
  capacityApiMessage,
} from "../../utils/capacityLimits";
import CapacityNoticeModal from "../../components/CapacityNoticeModal";

const EventMaster = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    eventName: "",
    email: "",
    password: "",
    name: "",
    isAdmin: true,
    isSuperAdmin: false,
    isActive: true,
    hostedBy: "",
    startDate: "",
    endDate: "",
    eventType: "",
    capacity: "",
    isEventActive: true,
    enteredBy: "",
    modifiedBy: "",
  });

  const [fileName1, setFileName1] = useState("");
  const [fileName2, setFileName2] = useState("");
  const user = useSelector((state) => state.auth.user);
  const handleFileChange = (e, setFileName, setFile) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name); // for display
      setFile(file); // for upload
    } else {
      setFileName("");
      setFile(null);
    }
  };

  const [eventTypeOptions, setEventTypeOptions] = useState([]);
  const [capacityOptions, setCapacityOptions] = useState([]);
  const [originalEventData, setOriginalEventData] = useState(null);
  const [eventLogo, setEventLogo] = useState(null);
  const [profilePic, setProfilePic] = useState(null);
  const [message, setMessage] = useState(null);
  const [events, setEvents] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editEventName, setEditEventName] = useState(""); // Store the event name for editing
  const [confirmation, setConfirmation] = useState(null); // For Activate/Deactivate confirmation
  const [capacityNotice, setCapacityNotice] = useState(null);
  const [editEventId, setEditEventId] = useState(null);
  const [activeTab, setActiveTab] = useState("all");

const fetchEvents = async () => {
  try {
    const data = await getAllEvents();
    console.log("Data received:", data);
    setEvents(data); // adjust if it's data.data
  } catch (error) {
    console.error("Error fetching events:", error);
    setMessage({ text: "Error fetching events", type: "error" });
  }
};

useEffect(() => {
  fetchEvents();
}, []);

  useEffect(() => {
    const fetchLookupValues = async () => {
      try {
        const response = await axios.get(getLookupValue());
        const lookupData = response.data.data;

        // Filter lookup values for eventType and capacity
        const eventTypeValues = lookupData
          .filter((item) => item.Lookup_Name === "Event Type")
          .map((item) => ({
            value: item.Lookup_Value_Name,
            label: item.Lookup_Value_Name,
          }));

        const capacityValues = lookupData
          .filter((item) => item.Lookup_Name === "Capacity")
          .map((item) => ({
            value: item.Lookup_Value_Name,
            label: item.Lookup_Value_Name,
          }));

        setEventTypeOptions(eventTypeValues);
        setCapacityOptions(capacityValues);
      } catch (error) {
        console.error("Error fetching lookup values:", error);
      }
    };

    fetchLookupValues();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const requiredFields = [
      { field: formData.eventName, label: "Event Name" },
      // { field: formData.name, label: "User Name" },
      // { field: formData.email, label: "Email" },
      { field: formData.startDate, label: "Start Date" },
      { field: formData.endDate, label: "End Date" },
      { field: formData.eventType, label: "Event Type" },
      { field: formData.capacity, label: "Capacity" },
    ];

    for (const { field, label } of requiredFields) {
      if (!field || field.trim() === "") {
        toast.error(`Please enter ${label}`);
        return; // Stop form submission
      }
    }

    try {
      if (isEditing) {
        const eventId = editEventId;
        const nextCap = parseCapacity(formData.capacity);
        if (nextCap != null) {
          try {
            const occRes = await axios.get(getOccasionsByEventId(eventId));
            const combined = sumOccasionCapacities(occRes.data?.data || []);
            if (combined > nextCap) {
              setCapacityNotice({
                title: "Cannot reduce event capacity",
                message: `Event capacity cannot be lower than the combined occasion capacity. Combined occasion capacity is ${combined}, but the event capacity is set to ${nextCap}. Increase event capacity or reduce occasion capacities first.`,
              });
              return;
            }
          } catch (occError) {
            if (occError.response?.status !== 404) {
              throw occError;
            }
          }
        }

        const hasNewFiles = eventLogo || profilePic;

        if (hasNewFiles) {
          // When new files present, send everything as FormData (same as before)
          const updatePayload = new FormData();
          updatePayload.append("eventName", formData.eventName);
          updatePayload.append("name", formData.name);
          updatePayload.append("email", formData.email);
          updatePayload.append("isAdmin", formData.isAdmin);
          updatePayload.append("isSuperAdmin", formData.isSuperAdmin);
          updatePayload.append("isActive", formData.isActive);
          updatePayload.append("isEventActive", formData.isEventActive);
          updatePayload.append("hostedBy", formData.hostedBy);
          updatePayload.append("startDate", formData.startDate);
          updatePayload.append("endDate", formData.endDate);
          updatePayload.append("eventType", formData.eventType);
          updatePayload.append("capacity", formData.capacity);
          updatePayload.append("modifiedBy", user?.name || "Unknown");
          if (eventLogo) updatePayload.append("eventLogo", eventLogo);
          if (profilePic) updatePayload.append("profilePic", profilePic);

          await axios.put(updateEventMaster(eventId), updatePayload, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "Content-Type": "multipart/form-data",
            },
          });
        } else {
          // No new files - only send changed, non-empty fields (keep existing functionality)
          const updatePayload = {};

          Object.keys(formData).forEach((key) => {
            const newValue = formData[key];
            const oldValue = originalEventData[key];

            // Add only if value changed and is not empty string or null/undefined
            if (
              newValue !== undefined &&
              newValue !== null &&
              newValue !== "" &&
              newValue !== oldValue
            ) {
              updatePayload[key] = newValue;
            }
          });

          await axios.put(updateEventMaster(eventId), updatePayload, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "Content-Type": "application/json",
            },
          });
        }

        await fetchEvents(); // Refresh events list after update

        toast.success("Event and User updated successfully!");
        handleClear();
      } else {
        const toBoolean = (value) => {
          if (typeof value === "boolean") return value;
          if (typeof value === "string") return value.toLowerCase() !== "false";
          return Boolean(value);
        };

        // Create new event (unchanged)
        const signupPayload = new FormData();
        signupPayload.append("eventName", formData.eventName);
        signupPayload.append("name", formData.name);
        signupPayload.append("email", formData.email);
        signupPayload.append("password", formData.password);
        signupPayload.append("isAdmin", formData.isAdmin);
        signupPayload.append("isSuperAdmin", formData.isSuperAdmin);
        signupPayload.append("isActive", toBoolean(formData.isActive));
        signupPayload.append(
          "isEventActive",
          toBoolean(formData.isEventActive)
        );

        signupPayload.append("hostedBy", formData.hostedBy);
        signupPayload.append("startDate", formData.startDate);
        signupPayload.append("endDate", formData.endDate);
        signupPayload.append("eventType", formData.eventType);
        signupPayload.append("capacity", formData.capacity);
        signupPayload.append("enteredBy", user?.name || "Unknown");
        if (eventLogo) signupPayload.append("eventLogo", eventLogo);
        if (profilePic) signupPayload.append("profilePic", profilePic);

        await axios.post(signUpEventAdmin(), signupPayload, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });

        await fetchEvents(); // Refresh events list after creation

        toast.success("Event and User created successfully!");
        handleClear();
      }
    } catch (error) {
      console.error("Error during submission:", error);
      if (error.response?.data?.code === "CAPACITY_EXCEEDED") {
        setCapacityNotice({
          title: "Cannot exceed capacity",
          message: capacityApiMessage(error),
        });
      } else if (error.response) {
        toast.error(
          `Error: ${error.response.data.message || "Failed to update."}`
        );
      } else {
        toast.error("Network or server error. Please try again.");
      }
    }
  };

const handleEdit = (event) => {
  setIsEditing(true);
  setEditEventId(event._id);
  setOriginalEventData(event); // Save original for comparison

  const firstAdmin = event.adminUsers?.[0]; // Grab first admin if exists

  setFormData({
    ...formData,
    eventName: event.eventName,
    name: firstAdmin?.name || "",      // âœ… Get name from first admin
    email: firstAdmin?.email || "",    // âœ… Get email from first admin
    password: "",
    isAdmin: event.isAdmin ?? true,    // Set defaults if needed
    isSuperAdmin: event.isSuperAdmin ?? false,
    isActive: event.isActive ?? true,
    isEventActive: event.isEventActive ?? true,
    hostedBy: event.hostedBy,
    startDate: formatDateForInput(event.startDate),
    endDate: formatDateForInput(event.endDate),
    eventType: event.eventType,
    capacity: event.capacity,
  });

  setFileName1(event.eventLogo ? event.eventLogo.split(/[/\\]/).pop() : "");
  setFileName2(event.profilePic ? event.profilePic.split(/[/\\]/).pop() : "");
  setEventLogo(null);
  setProfilePic(null);
  setIsEditing(true);
  setShowEventModal(true);
};


  const handleClear = () => {
    console.log("handleClear called");

  setFormData({
    eventName: "",
    email: "",
    password: "",
    name: "",
    isAdmin: true,
    isSuperAdmin: false,
    isActive: true,
    isEventActive: true, // âœ… restore it here
    hostedBy: "",
    startDate: "",
    endDate: "",
    eventType: "",
    capacity: "",
    enteredBy: "",
    modifiedBy: "",
  });

    setEventLogo(null);
    setProfilePic(null);
    setFileName1("");
    setFileName2("");
    setOriginalEventData(null);
    setEditEventName("");
    setEditEventId(null);
    setIsEditing(false);
    setConfirmation(null);
    setMessage(null);
    setShowEventModal(false);
  };

  const formatDateForInput = (isoString) => {
    if (!isoString) return "";
    return isoString.split("T")[0]; // take only YYYY-MM-DD part
  };

  const handleActivateDeactivate = (event) => {
    const newStatus = !event.isEventActive; // âœ… Use isEventActive here

    setConfirmation({
      message: `Are you sure you want to ${
        newStatus ? "enable" : "disable"
      } this event?`,
      newStatus,
      eventId: event._id,
    });
  };

  const confirmActivateDeactivate = async (confirmed) => {
    if (!confirmed) {
      setConfirmation(null);
      return;
    }

    try {
      const { eventId, newStatus } = confirmation;

      const updatePayload = {
        isEventActive: newStatus,
      };

      await axios.put(updateEventMaster(eventId), updatePayload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
        },
      });

      await fetchEvents();
      toast.success(
        `Event successfully ${newStatus ? "enabled" : "disabled"}.`
      );
    } catch (error) {
      console.error("Failed to update event status:", error);
      toast.error("Error updating event status.");
    } finally {
      setConfirmation(null);
    }
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [showEventModal, setShowEventModal] = useState(false);

  const openAddEventModal = () => {
    setFormData({
      eventName: "",
      email: "",
      password: "",
      name: "",
      isAdmin: true,
      isSuperAdmin: false,
      isActive: true,
      isEventActive: true,
      hostedBy: "",
      startDate: "",
      endDate: "",
      eventType: "",
      capacity: "",
      enteredBy: "",
      modifiedBy: "",
    });
    setEventLogo(null);
    setProfilePic(null);
    setFileName1("");
    setFileName2("");
    setOriginalEventData(null);
    setEditEventName("");
    setEditEventId(null);
    setIsEditing(false);
    setMessage(null);
    setShowEventModal(true);
  };

  const matchesSearch = (event) =>
    String(event.eventName || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase()) ||
    String(event.eventType || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase()) ||
    (event.hostedBy &&
      String(event.hostedBy)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()));

  const handleEnterEvent = (event) => {
    if (!isSuperAdminUser(user)) return;
    if (!event?._id) {
      toast.error("Unable to open this event.");
      return;
    }

    const allPages = EVENT_ACCESS_MODULES.map((module) => module.key);
    dispatch(
      setSelectedEvent({
        eventId: event._id,
        eventName: event.eventName,
        isAdmin: true,
        role: "Admin",
        isEventActive: event.isEventActive !== false,
        allowedPages: allPages,
        eventAllowedPages: allPages,
      })
    );
    toast.success(`Opened ${event.eventName || "event"}`);
    navigate("/dashboard", { replace: true });
  };

  const renderEventCard = (event, useIsActiveFlag = false) => {
    // Preserve original toggle label behavior per tab type
    const showActivateLabel = useIsActiveFlag
      ? Boolean(event.isActive)
      : !event.isEventActive;

    const metrics = event.metrics || {};
    const canOpen = isSuperAdminUser(user);

  return (
      <div
        className={`event-card${
          event.isEventActive === false ? " is-deactivated" : ""
        }${canOpen ? " is-openable" : ""}`}
        key={event._id}
        role={canOpen ? "button" : undefined}
        tabIndex={canOpen ? 0 : undefined}
        onClick={canOpen ? () => handleEnterEvent(event) : undefined}
        onKeyDown={
          canOpen
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleEnterEvent(event);
                }
              }
            : undefined
        }
        title={canOpen ? `Open ${event.eventName}` : undefined}
      >
        <div className="event-card-top">
          <img
            src={
              event.eventLogo
                ? `${FRONTEND_URL}/${event.eventLogo}`
                : function1
            }
            alt=""
            className="img-fluid-2"
          />
          <div className="event-card-heading">
            <div className="event-card-title-row">
              <strong className="event-card-title" title={event.eventName}>
                {event.eventName}
              </strong>
              {event.isEventActive === false ? (
                <span className="event-deactivated-badge">Deactivated</span>
              ) : (
                <span className="event-active-badge">Active</span>
              )}
            </div>
            {canOpen ? (
              <p className="event-card-open-hint">Click to open event</p>
            ) : null}
          </div>
        </div>

        <div className="event-card-body">
          <dl className="event-card-meta">
            <div className="event-card-meta-item">
              <dt className="event-card-meta-label">Host</dt>
              <dd className="event-card-meta-value">
                {event.hostedBy || "—"}
              </dd>
            </div>
            <div className="event-card-meta-item">
              <dt className="event-card-meta-label">Type</dt>
              <dd className="event-card-meta-value">
                {event.eventType || "—"}
              </dd>
            </div>
            <div className="event-card-meta-item">
              <dt className="event-card-meta-label">Dates</dt>
              <dd className="event-card-meta-value">
                {event.startDate
                  ? new Date(event.startDate).toLocaleDateString()
                  : "—"}
                {" – "}
                {event.endDate
                  ? new Date(event.endDate).toLocaleDateString()
                  : "—"}
              </dd>
            </div>
            <div className="event-card-meta-item">
              <dt className="event-card-meta-label">Capacity</dt>
              <dd className="event-card-meta-value">
                {event.capacity || "—"}
              </dd>
            </div>
          </dl>

          <div className="event-card-metrics">
            <span>
              <em>Guests</em>
              <strong>{metrics.guests ?? 0}</strong>
            </span>
            <span>
              <em>Occasions</em>
              <strong>{metrics.occasions ?? 0}</strong>
            </span>
            <span>
              <em>Users</em>
              <strong>{metrics.users ?? 0}</strong>
            </span>
            <span>
              <em>Hotels</em>
              <strong>{metrics.hotels ?? 0}</strong>
            </span>
          </div>

          <div
            className="event-actions"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="btn event-action-btn event-edit-btn"
              onClick={() => handleEdit(event)}
            >
              Edit
            </button>
            <button
              type="button"
              className={`btn event-action-btn ${
                showActivateLabel
                  ? "event-activate-btn"
                  : "event-deactivate-btn"
              }`}
              onClick={() => handleActivateDeactivate(event)}
            >
              {showActivateLabel ? "Activate" : "Deactivate"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="container-fluid event-page">
        {message && (
          <div
            className={`alert ${
              message.type === "success" ? "alert-success" : "alert-danger"
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="row g-4">
          {/* Events list */}
          <div className="col-12" id="sidebarColumn">
            <div className="event-sidebar event-master-list">
              <div className="event-master-header">
                <h2 className="event-master-title mb-0">Event Master</h2>
                <div className="event-master-toolbar ms-auto">
              <input
                type="text"
                    className="form-control event-master-search"
                placeholder="Search Event Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
                  <button
                    type="button"
                    className="btn event-toolbar-btn event-toolbar-primary"
                    onClick={openAddEventModal}
                  >
                    + Add Event
                  </button>
                  <button
                    type="button"
                    className="btn event-toolbar-btn event-toolbar-export"
                  >
                    Export to Excel <i className="bi bi-download ms-1"></i>
                  </button>
            </div>
          </div>

              <div className="event-master-scroll">
                <ul className="nav nav-tabs mb-4 event-filter-tabs" id="eventTabs" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      type="button"
                      className={`nav-link tab-all ${
                        activeTab === "all" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("all")}
                    >
                      All
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      type="button"
                      className={`nav-link tab-ongoing ${
                        activeTab === "ongoing" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("ongoing")}
                    >
                      Ongoing
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      type="button"
                      className={`nav-link tab-upcoming ${
                        activeTab === "upcoming-tab" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("upcoming-tab")}
                    >
                      Upcoming
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      type="button"
                      className={`nav-link tab-archived ${
                        activeTab === "archived-tab" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("archived-tab")}
                    >
                      Archived
                    </button>
                  </li>
                </ul>

                <div className="tab-content" id="eventTabsContent">
                  <div
                    className={`tab-pane fade ${
                      activeTab === "all" ? "show active" : ""
                    }`}
                    id="all-events"
                  >
                    <div className="event-card-grid">
                      {(() => {
                        const filteredEvents = events.filter(matchesSearch);

                        if (filteredEvents.length === 0) {
                          return (
                            <p className="text-center text-muted event-empty">
                              No events available.
                            </p>
                          );
                        }
                        return filteredEvents.map((event) =>
                          renderEventCard(event, false)
                        );
                      })()}
        </div>
                  </div>

                  <div
                    className={`tab-pane fade ${
                      activeTab === "ongoing" ? "show active" : ""
                    }`}
                    id="ongoing"
                  >
                    <div className="event-card-grid">
                      {(() => {
                        const filteredEvents = events
                          .filter((event) => event.isEventActive)
                          .filter(matchesSearch);

                        if (filteredEvents.length === 0) {
                          return (
                            <p className="text-center text-muted event-empty">
                              No ongoing events available.
                            </p>
                          );
                        }
                        return filteredEvents.map((event) =>
                          renderEventCard(event, true)
                        );
                      })()}
          </div>
                  </div>

                  <div
                    className={`tab-pane fade ${
                      activeTab === "upcoming-tab" ? "show active" : ""
                    }`}
                    id="upcoming-tab"
                  >
                    <div className="event-card-grid">
                      {(() => {
                        const filteredUpcomingEvents = events
                          .filter(
                            (event) => new Date(event.startDate) > new Date()
                          )
                          .filter(matchesSearch);

                        if (filteredUpcomingEvents.length === 0) {
                          return (
                            <p className="text-center text-muted event-empty">
                              No upcoming events available.
                            </p>
                          );
                        }
                        return filteredUpcomingEvents.map((event) =>
                          renderEventCard(event, false)
                        );
                      })()}
                    </div>
                  </div>

                  <div
                    className={`tab-pane fade ${
                      activeTab === "archived-tab" ? "show active" : ""
                    }`}
                    id="archived-tab"
                  >
                    <div className="event-card-grid">
                      {(() => {
                        const filteredArchivedEvents = events
                          .filter(
                            (event) => new Date(event.endDate) < new Date()
                          )
                          .filter(matchesSearch);

                        if (filteredArchivedEvents.length === 0) {
                          return (
                            <p className="text-center text-muted event-empty">
                              No archived events available.
                            </p>
                          );
                        }
                        return filteredArchivedEvents.map((event) =>
                          renderEventCard(event, false)
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {showEventModal && (
          <div
            className="modal fade show d-block event-form-modal"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {isEditing ? "Edit Event" : "Create New Event"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleClear}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <form
                    id="event-master-form"
                    onSubmit={handleSubmit}
                    className="event-form"
                  >
                    <div className="event-form-uploads">
                      <div className="media-upload text-center">
                        <label htmlFor="fileUpload1">
                          <i className="bi bi-camera-fill" />
                        </label>
                        <input
                          type="file"
                          id="fileUpload1"
                          className="d-none"
                          onChange={(e) =>
                            handleFileChange(e, setFileName1, setEventLogo)
                          }
                        />
                        <div className="text-muted small mt-2">{fileName1}</div>
                        <div className="upload-text">Event Logo</div>
                      </div>
                      <div className="media-upload text-center">
                        <label htmlFor="fileUpload2">
                          <i className="bi bi-camera-fill" />
                        </label>
                        <input
                          type="file"
                          id="fileUpload2"
                          className="d-none"
                          onChange={(e) =>
                            handleFileChange(e, setFileName2, setProfilePic)
                          }
                        />
                        <div className="text-muted small mt-2">{fileName2}</div>
                        <div className="upload-text">Admin Profile Photo</div>
                      </div>
                    </div>

                    <div className="row g-3">
                    <div className="col-md-6">
                        <label className="form-label">Event Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="eventName"
                        value={formData.eventName}
                        onChange={handleChange}
                        placeholder="Enter event name"
                        required
                      />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Hosted By</label>
                      <input
                        type="text"
                        name="hostedBy"
                        value={formData.hostedBy}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Enter host name"
                        required
                      />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Admin Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="form-control"
                          placeholder="Enter admin name"
                        required
                      />
                    </div>
                          <div className="col-md-6">
                        <label className="form-label">Admin Email</label>
                      <input
                          type="email"
                        className="form-control"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={isEditing}
                          placeholder="Enter admin login email"
                        required
                      />
                    </div>
                      <div className="col-md-6">
                        <label className="form-label">Password</label>
                      <input
                        type="password"
                        className="form-control"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        disabled={isEditing}
                        placeholder="Enter password"
                          required={!isEditing}
                      />
                    </div>
                      <div className="col-md-6">
                        <label className="form-label">Event Type</label>
                        <select
                          className="form-select"
                          name="eventType"
                          value={formData.eventType}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select Event Type</option>
                          {eventTypeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                  </div>
                    <div className="col-md-6">
                        <label className="form-label">Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Capacity</label>
                      <select
                        className="form-select"
                        name="capacity"
                        value={formData.capacity}
                        onChange={handleChange}
                      >
                          <option value="">Select Capacity</option>
                        {capacityOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </form>
              </div>
                <div className="modal-footer event-modal-actions">
                <button
                    type="button"
                    className="btn btn-cancel"
                  onClick={handleClear}
                >
                  Cancel
                  </button>
                  <button
                    type="submit"
                    form="event-master-form"
                    className="btn btn-save"
                  >
                    {isEditing ? "Update Event" : "Save Event"}
                  </button>
                </div>
                            </div>
                                  </div>
                                </div>
        )}

        {confirmation && (
          <div
            className="modal fade show d-block event-form-modal"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Action</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => confirmActivateDeactivate(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0 event-confirm-text">{confirmation.message}</p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn event-toolbar-btn event-toolbar-outline"
                    onClick={() => confirmActivateDeactivate(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`btn event-toolbar-btn ${
                      confirmation.newStatus
                        ? "event-activate-btn"
                        : "event-deactivate-btn"
                    }`}
                    onClick={() => confirmActivateDeactivate(true)}
                  >
                    {confirmation.newStatus ? "Enable" : "Disable"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <CapacityNoticeModal
          open={Boolean(capacityNotice)}
          title={capacityNotice?.title}
          message={capacityNotice?.message}
          onClose={() => setCapacityNotice(null)}
        />
      </div>
    </>
  );
};

export default EventMaster;
